import type { ReactNode } from 'react';
import type {
  ModuleAppointmentExtension,
  AppointmentExtensionContext,
  AppointmentDialogSlot,
  AppointmentExtensionValidation,
  AppointmentCreationInterception
} from './extension-types';
import { createExtensionBinder } from '@/extensions/createExtensionBinder';

export interface AppointmentExtensionApi {
  getSlotComponent: (slot: AppointmentDialogSlot) => ReactNode | null;
  buildContext: (currentFormValues?: Record<string, any>) => Promise<Record<string, any> | null>;
  buildMetadata: (currentFormValues?: Record<string, any>) => Promise<Record<string, any> | null>;
  validate: () => Promise<AppointmentExtensionValidation>;
  getInitialValues: () => Record<string, any>;
  onBeforeAppointmentCreated: (appointmentData: any) => Promise<AppointmentCreationInterception>;
  onAppointmentCreated: (appointment: any) => Promise<void>;
  onAppointmentError: (error: Error) => Promise<void>;
  onAppointmentCancelled: () => void;
}

const APPOINTMENT_DEFAULTS: AppointmentExtensionApi = {
  getSlotComponent: () => null,
  buildContext: async () => null,
  buildMetadata: async () => null,
  validate: async () => ({ valid: true }),
  getInitialValues: () => ({}),
  onBeforeAppointmentCreated: async () => ({}),
  onAppointmentCreated: async () => {},
  onAppointmentError: async () => {},
  onAppointmentCancelled: () => {}
};

export const bindAppointmentExtension = createExtensionBinder<
  ModuleAppointmentExtension,
  AppointmentExtensionContext,
  AppointmentExtensionApi
>(APPOINTMENT_DEFAULTS, (extension, context) => {
  const { formValues, moduleData } = context;

  return {
    ...APPOINTMENT_DEFAULTS,
    getSlotComponent: (slot: AppointmentDialogSlot) =>
      extension.getSlotComponents?.(slot, context) ?? null,

    buildContext: async (currentFormValues?: Record<string, any>) => {
      const valuesToUse = currentFormValues ?? formValues;
      const result = extension.buildContext?.(valuesToUse, moduleData);
      return result ?? null;
    },

    buildMetadata: async (currentFormValues?: Record<string, any>) => {
      if (!extension.buildMetadata) return null;
      const valuesToUse = currentFormValues ?? formValues;
      return extension.buildMetadata(valuesToUse, moduleData) ?? null;
    },

    validate: async (): Promise<AppointmentExtensionValidation> => {
      if (!extension.validate) return { valid: true };
      return extension.validate(formValues, moduleData);
    },

    getInitialValues: () => extension.getInitialValues?.(moduleData) ?? {},

    onBeforeAppointmentCreated: async (appointmentData: any): Promise<AppointmentCreationInterception> => {
      if (!extension.onBeforeAppointmentCreated) return {};
      const result = await extension.onBeforeAppointmentCreated(formValues, appointmentData, moduleData);
      if (result === undefined || result === null) return {};
      if (typeof result === 'boolean') return { skip: result };
      if (result && typeof result === 'object') return result as AppointmentCreationInterception;
      return {};
    },

    onAppointmentCreated: async (appointment: any) => {
      if (extension.onAppointmentCreated) {
        await extension.onAppointmentCreated(appointment, formValues, moduleData);
      }
    },

    onAppointmentError: async (error: Error) => {
      if (extension.onAppointmentError) {
        await extension.onAppointmentError(error, formValues, moduleData);
      }
    },

    onAppointmentCancelled: () => {
      extension.onAppointmentCancelled?.(formValues, moduleData);
    }
  };
});
